import { createContext, type ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import it from '@/locales/it.json';

export type Lang = 'it' | 'de' | 'fr' | 'en';
export const LANGS: Lang[] = ['it', 'de', 'fr', 'en'];

type Dict = Record<string, string>;

// Explicit loader map: Vite emits 3 deterministic chunks (de/fr/en). IT is eager for SSR/initial paint.
const LOCALE_LOADERS: Record<Exclude<Lang, 'it'>, () => Promise<{ default: Dict }>> = {
  de: () => import('@/locales/de.json'),
  fr: () => import('@/locales/fr.json'),
  en: () => import('@/locales/en.json'),
};

// TODO M37: Narrow `key` to `LocaleKey` (from @/types/locale-keys) once call sites are migrated.
// Typecheck probe (2026-05-03) returned 65 errors: dynamic template-literal keys in
// Funzionalita.tsx, Prezzi.tsx, VsFiduciarioTradizionale.tsx use `\`prefix.${number}.field\``
// patterns that TS cannot match against the literal union. Requires per-site cast or
// refactor of array-indexed key generation before the strict type can be enforced.
type Ctx = {
  lang: Lang;
  setLang: (l: Lang) => void;
  t: (key: string, vars?: Record<string, string | number>) => string;
};

function detect(): Lang {
  if (typeof navigator === 'undefined') return 'it';
  const code = (navigator.language || 'it').slice(0, 2).toLowerCase();
  return (LANGS as string[]).includes(code) ? (code as Lang) : 'it';
}

const I18nCtx = createContext<Ctx>({
  lang: 'it',
  setLang: () => {},
  t: (k) => k,
});

function initialLang(): Lang {
  if (typeof window === 'undefined') return 'it';
  try {
    const saved = localStorage.getItem('lang') as Lang | null;
    if (saved && LANGS.includes(saved)) return saved;
  } catch {
    /* localStorage unavailable */
  }
  return detect();
}

export function I18nProvider({ children }: { children: ReactNode }) {
  const [lang, setLangState] = useState<Lang>(initialLang);
  // Dicts in state so async load triggers re-render (previously useRef → stale UI in fallback italiano).
  const [dicts, setDicts] = useState<Map<Lang, Dict>>(() => new Map([['it', it as Dict]]));

  const loadLocale = useCallback(async (l: Lang) => {
    if (l === 'it') return;
    const loader = LOCALE_LOADERS[l];
    const mod = await loader();
    setDicts(curr => (curr.has(l) ? curr : new Map(curr).set(l, mod.default)));
  }, []);

  useEffect(() => {
    if (lang !== 'it' && !dicts.has(lang)) {
      loadLocale(lang);
    }
    // Predictive idle prefetch: warm navigator.language chunk so lang switch is instant.
    if (typeof window !== 'undefined') {
      const browser = (navigator.language || '').slice(0, 2).toLowerCase() as Lang;
      if (browser !== 'it' && (LANGS as string[]).includes(browser) && !dicts.has(browser)) {
        type IdleCB = (cb: () => void, opts?: { timeout?: number }) => number;
        const ric = (window as unknown as { requestIdleCallback?: IdleCB }).requestIdleCallback;
        const run = () => { void loadLocale(browser); };
        if (ric) ric(run, { timeout: 4000 });
        else setTimeout(run, 1800);
      }
    }
    // run once on mount with initial lang
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const setLang = useCallback(
    (l: Lang) => {
      localStorage.setItem('lang', l);
      document.documentElement.setAttribute('lang', l);
      if (dicts.has(l)) {
        setLangState(l);
      } else {
        loadLocale(l).finally(() => setLangState(l));
      }
    },
    [loadLocale, dicts]
  );

  useEffect(() => {
    document.documentElement.setAttribute('lang', lang);
  }, [lang]);

  const t = useCallback(
    (key: string, vars?: Record<string, string | number>) => {
      const dict = dicts.get(lang) ?? (it as Dict);
      let s = dict[key] ?? (it as Dict)[key] ?? key;
      if (vars) for (const k of Object.keys(vars)) s = s.replaceAll(`{${k}}`, String(vars[k]));
      return s;
    },
    [lang, dicts]
  );

  const value = useMemo(() => ({ lang, setLang, t }), [lang, setLang, t]);
  return <I18nCtx.Provider value={value}>{children}</I18nCtx.Provider>;
}

export function useI18n() {
  return useContext(I18nCtx);
}
